import { SPACE_FORGE_APPS } from '@confluence/named-routes/entry-points/routes.ts';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle.ts';
import { type ForgeUISpacePageExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';

import {
	ANALYTICS_KEY,
	ANALYTICS_KEY_MODERATE,
	QUESTIONS_KEY,
	CONNECT_ADDON_KEY,
	DATABASES_KEY,
	APPS_KEY,
} from './webItemCompleteKeys.ts';
import type {
	SpaceNavigationQuery$data,
	SpaceSidebarLinkType as SpaceNavigationQuerySpaceSidebarLinkType,
} from './__generated__/SpaceNavigationQuery.graphql.ts';
import { SpaceSidebarLinkType } from './__types__/BulkUpdateMainSpaceSidebarLinks.ts';

type SpaceNavigationQuery_spaceSidebarLinks_main = ApolloInteropMutableHelper<
	NonNullable<
		NonNullable<NonNullable<SpaceNavigationQuery$data['spaceSidebarLinks']>['main']>[number]
	>
>;

export const getAllAppLinks = (
	spaceKey: string,
	links: SpaceNavigationQuery_spaceSidebarLinks_main[],
	forgeApps: ForgeUISpacePageExtensionType[],
	isSpaceAdmin: boolean,
): {
	allAppLinks: (
		| ApolloInteropMutableHelper<{
				readonly canHide: boolean | null;
				readonly hidden: boolean;
				readonly icon: {
					readonly height: number | null;
					readonly isDefault: boolean | null;
					readonly path: string;
					readonly width: number | null;
				} | null;
				readonly iconClass: string | null;
				readonly id: string;
				readonly linkIdentifier: string | null;
				readonly position: number;
				readonly styleClass: string | null;
				readonly title: string | null;
				readonly tooltip: string | null;
				readonly type: SpaceNavigationQuerySpaceSidebarLinkType;
				readonly url: string | null;
				readonly webItemCompleteKey: string | null;
				readonly webItemKey: string | null;
		  }>
		| {
				url: string;
				title: string;
				icon: {
					width: null;
					height: null;
					isDefault: null;
					path: any;
				};
				canHide: ApolloInteropMutableHelper<boolean | null>;
				hidden: ApolloInteropMutableHelper<boolean>;
				iconClass: ApolloInteropMutableHelper<string | null>;
				id: string;
				linkIdentifier: ApolloInteropMutableHelper<string | null>;
				position: number;
				styleClass: ApolloInteropMutableHelper<string | null>;
				tooltip: ApolloInteropMutableHelper<string | null>;
				type: ApolloInteropMutableHelper<SpaceNavigationQuerySpaceSidebarLinkType>;
				webItemCompleteKey: ApolloInteropMutableHelper<string | null>;
				webItemKey: ApolloInteropMutableHelper<string | null>;
		  }
	)[];
	allVisibleAppLinks: (
		| ApolloInteropMutableHelper<{
				readonly canHide: boolean | null;
				readonly hidden: boolean;
				readonly icon: {
					readonly height: number | null;
					readonly isDefault: boolean | null;
					readonly path: string;
					readonly width: number | null;
				} | null;
				readonly iconClass: string | null;
				readonly id: string;
				readonly linkIdentifier: string | null;
				readonly position: number;
				readonly styleClass: string | null;
				readonly title: string | null;
				readonly tooltip: string | null;
				readonly type: SpaceNavigationQuerySpaceSidebarLinkType;
				readonly url: string | null;
				readonly webItemCompleteKey: string | null;
				readonly webItemKey: string | null;
		  }>
		| {
				url: string;
				title: string;
				icon: {
					width: null;
					height: null;
					isDefault: null;
					path: any;
				};
				canHide: ApolloInteropMutableHelper<boolean | null>;
				hidden: ApolloInteropMutableHelper<boolean>;
				iconClass: ApolloInteropMutableHelper<string | null>;
				id: string;
				linkIdentifier: ApolloInteropMutableHelper<string | null>;
				position: number;
				styleClass: ApolloInteropMutableHelper<string | null>;
				tooltip: ApolloInteropMutableHelper<string | null>;
				type: ApolloInteropMutableHelper<SpaceNavigationQuerySpaceSidebarLinkType>;
				webItemCompleteKey: ApolloInteropMutableHelper<string | null>;
				webItemKey: ApolloInteropMutableHelper<string | null>;
		  }
	)[];
	shouldRenderSpaceApps: boolean;
} => {
	const forgeAppsMap: Map<string, ForgeUISpacePageExtensionType> = forgeApps.reduce((acc, app) => {
		return acc.set(app.id, app);
	}, new Map());

	const allAppLinks = links
		.filter(
			(link) =>
				(isConnectApp(link) && !isBuiltInItem(link)) ||
				(isForgeApp(link) && forgeAppsMap.has(link.linkIdentifier as string)),
		)
		// Fill in links for Forge apps
		.map((link) => {
			if (!isForgeApp(link)) {
				return link;
			}
			const forgeApp = forgeAppsMap.get(
				link.linkIdentifier as string,
			) as ForgeUISpacePageExtensionType;

			return {
				...link,
				url: getForgeURL(spaceKey, forgeApp),
				title: extensionTitle(forgeApp),
				icon: {
					width: null,
					height: null,
					isDefault: null,
					path: forgeApp.properties.icon || '',
				},
			};
		})
		.sort((a, b) => Number(a.position) - Number(b.position));

	// Adding forge apps that are not in the links yet
	// This could be possible for new installed forge apps
	const allForgeLinksIds = allAppLinks.filter(isForgeApp).map((link) => link.linkIdentifier);

	forgeApps.forEach((forgeApp) => {
		if (!allForgeLinksIds.includes(forgeApp.id)) {
			const forgeAppLink = {
				id: forgeApp.id,
				type: SpaceSidebarLinkType.FORGE,
				linkIdentifier: forgeApp.id,
				position: 0,
				hidden: false,
				webItemKey: null,
				webItemCompleteKey: null,
				title: extensionTitle(forgeApp),
				url: getForgeURL(spaceKey, forgeApp),
				styleClass: null,
				icon: {
					path: forgeApp.properties.icon || '',
					width: null,
					height: null,
					isDefault: null,
				},
				iconClass: null,
				canHide: null,
				tooltip: null,
				properties: forgeApp.properties,
			};
			allAppLinks.push(forgeAppLink);
		}
	});

	const appsHidden =
		links.filter(({ webItemCompleteKey, hidden }) => webItemCompleteKey === APPS_KEY && hidden)
			.length > 0;

	const allVisibleAppLinks = allAppLinks.filter((link) => !link.hidden);
	const shouldRenderSpaceApps =
		!appsHidden && !(allAppLinks.length == 0 || (allVisibleAppLinks.length == 0 && !isSpaceAdmin));
	return {
		allAppLinks,
		allVisibleAppLinks,
		shouldRenderSpaceApps,
	};
};

export const getForgeURL = (spaceKey: string, app: ForgeUISpacePageExtensionType): string => {
	return SPACE_FORGE_APPS.toUrl({
		spaceKey,
		appId: app.appId,
		envId: app.environmentId,
		forgeManifestRoute: app.properties.route,
	});
};

export const isConnectApp = (link: any): any => {
	return link.webItemCompleteKey?.startsWith(CONNECT_ADDON_KEY);
};

export const isForgeApp = (link: any): boolean => {
	return link.type === SpaceSidebarLinkType.FORGE && link.linkIdentifier;
};

const isBuiltInItem = (link): boolean => {
	const builtInItems = [ANALYTICS_KEY, ANALYTICS_KEY_MODERATE, QUESTIONS_KEY, DATABASES_KEY];

	return builtInItems.includes(link.webItemCompleteKey);
};
